/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, CircularProgress, Typography } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  EventCreate,
  useGetMassTimeseriesQuery,
} from "src/store/api/generatedApi";
import { ScaleWeightsChart } from "./ScaleWeightsChart";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";

interface MassDataPanelProps {
  startIso: string;
  endIso: string;
  videoStart: Dayjs;
  location: string;
  events: ScrapEyesEvent[];
  candidateEvents: EventCreate[];
  includeCandidateEvents?: boolean;
}

export const MassDataPanel = ({
  startIso,
  endIso,
  videoStart,
  location,
  events,
  candidateEvents,
  includeCandidateEvents,
}: MassDataPanelProps) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const { extra_data: extraData } = useScrapEyeMetadata();

  const { data, isLoading, isError } = useGetMassTimeseriesQuery(
    {
      tenantName,
      start: startIso,
      end: endIso,
      location,
      include: includeCandidateEvents ? ["candidate_events"] : undefined,
    },
    {
      skip: !extraData?.includes("mass_data"),
    }
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 180,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 180,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("unableToLoadScaleWeights")}
        </Typography>
      </Box>
    );
  }
  if (!data || data.series.length === 0) {
    return null;
  }

  const candidates = includeCandidateEvents
    ? (data.candidate_events ?? [])
    : candidateEvents;

  return (
    <Box sx={{ overflow: "visible", width: "100%" }}>
      <ScaleWeightsChart
        data={data}
        videoStart={videoStart}
        events={events}
        candidateEvents={candidates}
      />
    </Box>
  );
};
